/**
 * Pack products block ("Pack products (metafield)").
 *
 * Two responsibilities:
 *   1. <pack-products> custom element: optional autoplay for the inner <scroll-carousel>
 *      (the theme's scroll-carousel has no autoplay of its own). Pauses on hover / focus /
 *      drag, when the tab is hidden and when the block is out of the viewport.
 *   2. A global capture-phase submit listener that injects the `properties[_pack_discount]`
 *      line item property into any product form living inside an element that carries a
 *      `data-pack-discount` attribute (the quick-buy drawer, whose form is fetched on demand).
 *      This is what makes the Shopify discount function apply the % to that cart line.
 */
(function () {
  'use strict';

  var PROP_NAME = 'properties[_pack_discount]';

  /* ------------------------------------------------------------------ *
   * 1. Autoplay carousel
   * ------------------------------------------------------------------ */
  if (!customElements.get('pack-products')) {
    customElements.define(
      'pack-products',
      class PackProducts extends HTMLElement {
        connectedCallback() {
          this._timer = null;
          this._carousel =
            this.querySelector('scroll-carousel') ||
            document.getElementById(this.getAttribute('carousel'));

          var speed = parseFloat(this.getAttribute('autoplay'));
          if (!this._carousel || !(speed > 0)) return;

          this._interval = speed * 1000;
          this._bindAutoplay();
        }

        disconnectedCallback() {
          this._stop();
        }

        _bindAutoplay() {
          var self = this;

          this.addEventListener('mouseenter', function () { self._stop(); });
          this.addEventListener('mouseleave', function () { self._start(); });
          this.addEventListener('focusin', function () { self._stop(); });
          this.addEventListener('focusout', function () { self._start(); });
          this.addEventListener('pointerdown', function () { self._stop(); });
          this.addEventListener('touchstart', function () { self._stop(); }, { passive: true });

          document.addEventListener('visibilitychange', function () {
            if (document.hidden) self._stop();
            else self._start();
          });

          if ('IntersectionObserver' in window) {
            new IntersectionObserver(
              function (entries) {
                entries.forEach(function (entry) {
                  if (entry.isIntersecting) self._start();
                  else self._stop();
                });
              },
              { threshold: 0.2 }
            ).observe(this);
          } else {
            this._start();
          }
        }

        _start() {
          if (this._timer || !this._carousel) return;
          var self = this;
          this._timer = setInterval(function () { self._advance(); }, this._interval);
        }

        _stop() {
          if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
          }
        }

        _advance() {
          var el = this._carousel;
          var card = el.querySelector('.horizontal-product');
          if (!card) return;

          var list = el.querySelector('.horizontal-product-list');
          var gap = list ? parseFloat(getComputedStyle(list).columnGap) : 0;
          if (!(gap >= 0)) gap = 0;

          var step = card.getBoundingClientRect().width + gap;
          var maxScroll = el.scrollWidth - el.clientWidth;

          if (el.scrollLeft >= maxScroll - 2) {
            el.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            el.scrollBy({ left: step, behavior: 'smooth' });
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * 2. Inject the discount line item property into pack forms
   * ------------------------------------------------------------------ */
  if (!window.__packDiscountSubmitHooked) {
    window.__packDiscountSubmitHooked = true;

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        var host = form.closest('[data-pack-discount]');
        if (!host) return;

        var pct = host.getAttribute('data-pack-discount');
        if (!pct || !(parseFloat(pct) > 0)) return;

        // Skip if the property is already present (e.g. printed by Liquid).
        if (form.querySelector('input[name="' + PROP_NAME + '"]')) return;

        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = PROP_NAME;
        input.value = pct;
        form.appendChild(input);
      },
      true // capture: run before the product-form's own submit handler
    );
  }
})();
